import { z } from 'zod';
import { isIsoDay } from '../utils/checklist.js';
import { MAX_TASK_REPEAT_INTERVAL, TASK_REPEAT_UNITS } from '../types/event-ops.js';
import { VENDOR_TIME_KINDS } from '../utils/vendorTimes.js';
// ─── Timeline Validation ────────────────────────────────────────────────────
// The bulk/nested shape (createTimeline's entries array, JSON import): the
// caller lays out the whole list at once and gives each entry its own
// sortOrder directly. There is no sibling to anchor to yet — nothing has an
// id until the request is committed.
export const createTimelineEntrySchema = z.object({
    time: z.string().max(50).optional(),
    title: z.string().min(1).max(500),
    location: z.string().max(500).optional(),
    vendorId: z.string().uuid().optional(),
    vendorNote: z.string().max(500).optional(),
    notes: z.string().max(2000).optional(),
    sortOrder: z.number().int().optional(),
});
// POST /timelines/sections/:sectionId/entries — adding a single entry to an
// existing section. This endpoint always computes its own rank (by time, or
// right after afterEntryId), so sortOrder has no meaning here; accepting it
// anyway would let a caller silently reintroduce the tie/gap bug the
// computed-rank logic exists to prevent.
export const addTimelineEntrySchema = createTimelineEntrySchema.omit({ sortOrder: true }).extend({
    // Explicit manual placement — insert right after this sibling entry
    // instead of positioning by time. Takes priority over the time-based
    // auto-sort.
    afterEntryId: z.string().uuid().optional(),
    // The person this entry is theirs to run (the timeline.assigned automation).
    // Only on the single-entry routes, which announce it: the nested `entries`
    // of a whole new timeline are created without an assignee.
    assignedToUserId: z.string().uuid().nullable().optional(),
});
export const updateTimelineEntrySchema = z.object({
    time: z.string().max(50).nullable().optional(),
    title: z.string().min(1).max(500).optional(),
    location: z.string().max(500).nullable().optional(),
    vendorId: z.string().uuid().nullable().optional(),
    vendorNote: z.string().max(500).nullable().optional(),
    assignedToUserId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const createTimelineSectionSchema = z.object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    sortOrder: z.number().int().optional(),
    entries: z.array(createTimelineEntrySchema).optional(),
});
export const updateTimelineSectionSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const createTimelineSchema = z.object({
    eventId: z.string().uuid().optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).optional(),
    title: z.string().max(500).optional(),
    description: z.string().max(2000).optional(),
    rainPlanNotes: z.string().max(2000).optional(),
    eventDate: z.string().datetime().optional(),
    eventTypeId: z.string().uuid().nullable().optional(),
    sections: z.array(createTimelineSectionSchema).optional(),
});
export const updateTimelineSchema = z.object({
    title: z.string().max(500).nullable().optional(),
    templateName: z.string().max(255).nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
    rainPlanNotes: z.string().max(2000).nullable().optional(),
    eventDate: z.string().datetime().nullable().optional(),
    eventTypeId: z.string().uuid().nullable().optional(),
});
// ─── Checklist Validation ───────────────────────────────────────────────────
/**
 * A task's typed assignee (the old free-text field), stored trimmed, and a
 * blank one stored as null — so "nobody assigned" is `assignee IS NULL` and
 * the database can count it, rather than every reader trimming.
 */
const typedAssignee = z
    .string()
    .max(255)
    .transform((v) => v.trim() || null);
/** A repeat's unit, and how many of them — null or absent for a task that does not repeat. */
const repeatUnit = z.enum(TASK_REPEAT_UNITS);
const repeatInterval = z.number().int().min(1).max(MAX_TASK_REPEAT_INTERVAL);
/** A gap with no unit says nothing; a unit alone means every one of it. */
const repeatNeedsUnit = (v) => v.repeatInterval == null || v.repeatUnit != null;
const repeatNeedsUnitMessage = {
    message: 'Choose how often the task repeats',
    path: ['repeatUnit'],
};
export const createChecklistItemSchema = z
    .object({
    title: z.string().min(1).max(500),
    assignee: typedAssignee.optional(),
    isDone: z.boolean().optional(),
    status: z.enum(['TODO', 'IN_PROGRESS', 'DONE', 'BLOCKED']).optional(),
    dueDate: z.string().optional(),
    assignedToUserId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    sortOrder: z.number().int().optional(),
    dueDateOffsetDays: z.number().int().nullable().optional(),
    repeatUnit: repeatUnit.nullable().optional(),
    repeatInterval: repeatInterval.nullable().optional(),
})
    .refine(repeatNeedsUnit, repeatNeedsUnitMessage);
export const updateChecklistItemSchema = z
    .object({
    title: z.string().min(1).max(500).optional(),
    assignee: typedAssignee.nullable().optional(),
    isDone: z.boolean().optional(),
    status: z.enum(['TODO', 'IN_PROGRESS', 'DONE', 'BLOCKED']).optional(),
    dueDate: z.string().nullable().optional(),
    assignedToUserId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().optional(),
    dueDateOffsetDays: z.number().int().nullable().optional(),
    repeatUnit: repeatUnit.nullable().optional(),
    repeatInterval: repeatInterval.nullable().optional(),
})
    .refine(repeatNeedsUnit, repeatNeedsUnitMessage);
export const createChecklistSectionSchema = z.object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    sortOrder: z.number().int().optional(),
    items: z.array(createChecklistItemSchema).optional(),
});
export const updateChecklistSectionSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const createChecklistSchema = z.object({
    eventId: z.string().uuid().optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).optional(),
    title: z.string().max(500).optional(),
    description: z.string().max(2000).optional(),
    sections: z.array(createChecklistSectionSchema).optional(),
});
export const updateChecklistSchema = z.object({
    title: z.string().max(500).optional(),
    /** A template's name as the Templates page shows it — renamed with its title. */
    templateName: z.string().max(255).nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
});
export const toggleChecklistItemSchema = z.object({
    isDone: z.boolean(),
});
const isoDay = z.string().refine(isIsoDay, 'Expected a real calendar day, YYYY-MM-DD');
export const bulkTaskSchema = z
    .object({
    itemIds: z.array(z.string().uuid()).min(1).max(200),
    action: z.enum(['complete', 'reopen', 'assign', 'due', 'delete']),
    assignedToUserId: z.string().uuid().nullable().optional(),
    dueDate: isoDay.nullable().optional(),
})
    .refine((v) => v.action !== 'assign' || v.assignedToUserId !== undefined, {
    message: 'assignedToUserId is required to assign',
    path: ['assignedToUserId'],
})
    .refine((v) => v.action !== 'due' || v.dueDate !== undefined, {
    message: 'dueDate is required to set a due date',
    path: ['dueDate'],
});
/**
 * Moving an event's template tasks after its date changed. `from` is the start
 * the event had before — the tasks still sitting where their offset put them
 * from it are the ones that follow the event.
 */
export const TASK_DATE_SHIFT_CHUNK = 500;
export const taskDateShiftQuerySchema = z.object({
    from: z.string().datetime({ offset: true }),
});
export const taskDateShiftSchema = z.object({
    from: z.string().datetime({ offset: true }),
    /** The start the preview worked from — `TaskDateShiftPreview.to`. */
    to: z.string().datetime({ offset: true }),
    /** One request's worth; a longer selection is sent in chunks of this size. */
    itemIds: z.array(z.string().uuid()).min(1).max(TASK_DATE_SHIFT_CHUNK),
});
/** The full new order of a list's sections. */
export const reorderChecklistSectionsSchema = z.object({
    sectionIds: z.array(z.string().uuid()).min(1).max(500),
});
/** The full new order of one section's items — ids from another section of the same list move into it. */
export const reorderChecklistItemsSchema = z.object({
    itemIds: z.array(z.string().uuid()).max(1000),
});
/**
 * PUT /timelines/:id/section-order — every section of the timeline, once, in
 * its new order, and the order the page saw before the drop (`expected`); the
 * server refuses (409) when that is no longer the stored order.
 */
export const reorderTimelineSectionsSchema = z.object({
    sectionIds: z.array(z.string().uuid()).min(1).max(500),
    expected: z.array(z.string().uuid()).min(1).max(500),
});
/**
 * PUT /timelines/:id/entry-order — the new entry order of one section, or of
 * the two an entry moved between, each with the order the page saw before
 * (`expected`). Both sections commit together or not at all.
 */
export const reorderTimelineEntriesSchema = z.object({
    sections: z
        .array(z.object({
        sectionId: z.string().uuid(),
        entryIds: z.array(z.string().uuid()).max(1000),
        expected: z.array(z.string().uuid()).max(1000),
    }))
        .min(1)
        .max(2),
});
// ─── Kibuddim Validation ───────────────────────────────────────────────────
// A role as the nested `roles` of a whole new kibuddim list: no assignee, since
// nothing there announces one.
export const kibuddimRoleFieldsSchema = z.object({
    roleName: z.string().min(1).max(500),
    roleNameEn: z.string().max(500).optional(),
    assignee: z.string().max(255).optional(),
    notes: z.string().max(2000).optional(),
    sortOrder: z.number().int().optional(),
});
// POST /kibuddim/sections/:sectionId/roles — one role, which may be given to a
// person in the directory (the kibuddim.assigned automation); `assignee` stays
// the free-text name for somebody who is not.
export const createKibuddimRoleSchema = kibuddimRoleFieldsSchema.extend({
    assignedToUserId: z.string().uuid().nullable().optional(),
});
export const updateKibuddimRoleSchema = z.object({
    roleName: z.string().min(1).max(500).optional(),
    roleNameEn: z.string().max(500).nullable().optional(),
    assignee: z.string().max(255).nullable().optional(),
    assignedToUserId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const createKibuddimSectionSchema = z.object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    sortOrder: z.number().int().optional(),
    roles: z.array(kibuddimRoleFieldsSchema).optional(),
});
export const updateKibuddimSectionSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const createKibuddimSchema = z.object({
    eventId: z.string().uuid().optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).optional(),
    title: z.string().max(500).optional(),
    description: z.string().max(2000).optional(),
    sections: z.array(createKibuddimSectionSchema).optional(),
});
export const updateKibuddimSchema = z.object({
    title: z.string().max(500).optional(),
    description: z.string().max(2000).nullable().optional(),
});
// ─── Client tasks (the portal) ─────────────────────────────────────────────
export const createClientTaskSchema = z.object({
    title: z.string().trim().min(1).max(500),
    dueDate: isoDay.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const updateClientTaskSchema = createClientTaskSchema.partial();
// ─── Vendor times on the timeline ───────────────────────────────────────────
/**
 * `POST /event-ops/timelines/:id/vendor-times` — which bookings' times to put
 * on the timeline. The times themselves are read from the bookings on the
 * server, never sent. No section: the timeline's "Vendors" section, made if
 * missing.
 */
export const addTimelineVendorTimesSchema = z.object({
    items: z
        .array(z
        .object({
        linkId: z.string().uuid(),
        kind: z.enum(VENDOR_TIME_KINDS),
    })
        .strict())
        .min(1, 'Choose at least one time')
        .max(200),
    sectionId: z.string().uuid().optional(),
});
//# sourceMappingURL=event-ops.js.map